import { shortUuid } from '@tunasong/models'
import { AssistantAvatarContext, Chat } from '@tunasong/plugin-chat/ui'
import type { ChatProps } from '@tunasong/plugin-chat/ui'
import { nlpApi, useThunkDispatch } from '@tunasong/redux'
import type { Agent, ColorName, Comment, Persisted } from '@tunasong/schemas'
import { Avatar, getColorCSS } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { Node } from 'slate'
import { resolveAgentVisuals } from './agent-visuals.js'
import { LiveAgentResponse } from './live-agent-response.js'
import { useInFlightAgentStream } from './use-in-flight-agent-stream.js'

/**
 * Same shape as ChatProps but with `element` narrowed to Agent — agentStream
 * only makes sense for agent entities, so we lock the type at the seam rather
 * than runtime-guard inside the handler.
 *
 * `pendingDraft` / `onDraftConsumed` / `chatBoxId` are passthroughs to the
 * underlying ChatBox so callers (the sidebar empty-state header) can pre-fill
 * the input and wire aria-describedby without reaching into Chat's internals.
 */
export interface AgentChatProps extends Omit<ChatProps, 'element'> {
  element: Persisted<Agent> | Agent
}

/**
 * Read the text body of a chat comment by walking its Slate tree.
 * Returns '' for empty or whitespace-only comments so callers can short-circuit.
 */
const commentText = (comment: Comment): string =>
  (comment.children ?? [])
    .map(child => Node.string(child as never))
    .join('\n')
    .trim()

/**
 * Wraps Chat with agent-specific behavior: when the LOCAL user posts a comment
 * via the ChatBox, fire nlpApi.agentStream so runAgent runs server-side. The
 * agent's response is persisted as another comment child by runAgent and
 * arrives at this thread via the normal entity-push pipeline — so we do NOT
 * need to render the streamed text here. (Streaming-bubble UI is a follow-up.)
 *
 * Skipping conditions:
 * - `userId === 'assistant'` — the comment is the agent's own response; firing
 *   would create an invocation loop.
 * - empty text — submit-on-enter and similar UX bugs can produce empty
 *   comments; we don't spend an LLM round-trip on them.
 */
export const AgentChat: FC<AgentChatProps> = props => {
  const { element: agent } = props
  const dispatch = useThunkDispatch()

  // Subscribe to the in-flight `agentStream` cache entry for this agent so we
  // can render the live response below the persisted-comment feed. The hook
  // returns `null` when no stream is in flight (or when the cache entry has
  // evicted post-completion via `keepUnusedDataFor: 0`).
  const liveStream = useInFlightAgentStream(agent.id)

  // Per-thread avatar override: `userId === 'assistant'` comments in this
  // chat are from this agent, so render the agent's resolved icon + color.
  // The factory is stable across renders for the same agent identity — React
  // Compiler memoises the body, so no manual useCallback/useMemo needed.
  const { color, Icon } = resolveAgentVisuals(agent)
  const backgroundColor = getColorCSS(color as ColorName)
  const AgentAvatar: FC = () => (
    <Avatar style={{ backgroundColor, color: 'var(--tuna-content-on-color, #fff)' }}>
      <Icon aria-hidden="true" />
    </Avatar>
  )

  const handleComment = (comment: Comment) => {
    if (comment.userId === 'assistant') {
      return
    }
    const text = commentText(comment)
    if (text.length === 0) {
      return
    }
    const agentId = agent.id
    if (!agentId) {
      return
    }
    // `requestId` is required by `AgentStreamPayload` so RTK Query keys
    // don't collapse across repeated chat submissions (an identical
    // (agentId, message, trigger) tuple would otherwise hit the cached
    // stream instead of running the agent again). Fresh nonce per submit.
    void dispatch(
      nlpApi.endpoints.agentStream.initiate({
        agentId,
        message: text,
        trigger: { type: 'chat' },
        // requestId is required on AgentStreamPayload — unique per submit so
        // RTK Query cache keys don't collapse across re-asks.
        requestId: shortUuid(),
      })
    )
  }

  // Cast: ChatProps.element is Entity (wider). Agent is in the Entity union so
  // the runtime value is fine; the cast tells TS to forward the narrower type
  // through Chat's wider slot.
  return (
    <AssistantAvatarContext.Provider value={AgentAvatar}>
      <Chat {...(props as ChatProps)} onComment={handleComment} />
      {liveStream ? <LiveAgentResponse stream={liveStream} accentColor={backgroundColor} /> : null}
    </AssistantAvatarContext.Provider>
  )
}
